import type { CSSProperties, ReactNode } from 'react';
import { YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES } from '../../../theme/current-theme-overrides';

export const sourceFaithfulPalette = {
  primary: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-primary'],
  primaryHover: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-primary-hover'],
  ironGrey: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-irongrey'],
  strawberry: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-strawberry'],
  coral: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-coral'],
  aquamarine: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-aquamarine'],
  jungle: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-jungle'],
  backgroundLight: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-background-light'],
  surfaceLight: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-surface-light'],
  border: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-border'],
  muted: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-muted'],
  textSecondary: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-text-secondary'],
  textTertiary: YSTNJ_CURRENT_THEME_TAILWIND_OVERRIDES['--color-text-tertiary'],
} as const;

export const sourceFaithfulChartAxisTick = {
  fill: sourceFaithfulPalette.textSecondary,
  fontSize: 12,
  fontFamily: 'var(--font-body)',
};

export const sourceFaithfulChartTooltipStyle: CSSProperties = {
  backgroundColor: sourceFaithfulPalette.surfaceLight,
  border: `1px solid ${sourceFaithfulPalette.border}`,
  borderRadius: '10px',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
  color: sourceFaithfulPalette.ironGrey,
  fontFamily: 'var(--font-body)',
  fontSize: '13px',
};

export const sourceFaithfulChartLegendStyle: CSSProperties = {
  color: sourceFaithfulPalette.textSecondary,
  fontFamily: 'var(--font-body)',
  fontSize: '12px',
  paddingTop: '12px',
};

export const formatCurrencyThousands = (value: number | string): string => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }
  return `$${numeric.toLocaleString()}K`;
};

export const formatPercent = (value: number | string): string => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }
  return `${numeric}%`;
};

type SourceFaithfulChartCardProps = {
  title: string;
  chartAriaLabel: string;
  children: ReactNode;
  subtitle?: string;
  testId?: string;
  className?: string;
  chartHeightPx?: number;
};

export function SourceFaithfulChartCard({
  title,
  chartAriaLabel,
  children,
  subtitle,
  testId,
  className,
  chartHeightPx = 280,
}: SourceFaithfulChartCardProps) {
  return (
    <section
      data-testid={testId}
      className={`rounded-[12px] border border-border bg-surface-light p-6 shadow-sm ${className ?? ''}`.trim()}
    >
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-iron-grey">{title}</h2>
        {subtitle ? (
          <p className="max-w-[28rem] text-right text-sm text-text-secondary">{subtitle}</p>
        ) : null}
      </div>
      <div role="img" aria-label={chartAriaLabel} style={{ height: `${chartHeightPx}px` }}>
        {children}
      </div>
    </section>
  );
}
